<script setup lang="ts">
import { computed, type Component } from "vue";
import { Check, Clipboard, Download, Info, SearchX, Settings2 } from "lucide-vue-next";
import { Tooltip } from "@/components/ui/tooltip";
import { EmptyState } from "@/components/episto-ui/empty-state";
import { LoadingState } from "@/components/episto-ui/loading-state";
import { useCardDownload } from "@/composables/useCardDownload";
import ResultsStyledBlock from "../ResultsStyledBlock.vue";
import CardTypeIcon from "./CardTypeIcon.vue";
import CardWidthToggle from "./CardWidthToggle.vue";
import ResultsV2CardIndicators from "./ResultsV2CardIndicators.vue";
import { cardSubtype, cardTypeMeta } from "../constants";

interface ShellCard {
  id: number;
  card_type?: string;
  content?: { sub_type?: string };
  options?: {
    comment?: string;
    comment_style?: string;
    custom_name_title?: { name?: string; title?: string } | null;
    width?: string;
  } | null;
}

export type AnalysisCardState = "unconfigured" | "loading" | "empty" | "ready";

const STATES: Record<Exclude<AnalysisCardState, "ready" | "loading">, { icon: Component; title: string; description?: string }> = {
  unconfigured: { icon: Settings2, title: "Configure this card in the panel." },
  empty: {
    icon: SearchX,
    title: "No data for this configuration.",
    description: "Check the selected questions and populations.",
  },
};

const props = withDefaults(
  defineProps<{
    card: ShellCard;
    readonly?: boolean;
    name?: string;
    description?: string;
    respondents?: number | null;
    respondentsHint?: string;
    weighted?: boolean;
    state?: AnalysisCardState;
    stateMessage?: string;
  }>(),
  {
    readonly: false,
    name: "",
    description: "",
    respondents: null,
    respondentsHint: "",
    weighted: false,
    state: "ready",
    stateMessage: "",
  },
);

const emptyState = computed(() => (props.state === "ready" || props.state === "loading" ? null : STATES[props.state]));

const { downloadCardAsImage, copyCardToClipboard, copied } = useCardDownload();

const meta = computed(() => cardTypeMeta(cardSubtype(props.card)));
const displayName = computed(
  () => props.card.options?.custom_name_title?.name || props.name || meta.value?.label || "",
);

const customTitle = computed(() => props.card.options?.custom_name_title?.title || "");

const comment = computed(() => {
  const raw = props.card.options?.comment || "";
  return raw.replace(/<[^>]*>/g, "").trim() ? raw : "";
});
</script>

<template>
  <div class="group flex h-full flex-col gap-3 p-4">
    <div class="flex items-start justify-between gap-2">
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <div class="flex items-center gap-2">
          <CardTypeIcon :card="card" />
          <span class="truncate text-13 font-semibold">{{ displayName }}</span>
          <Tooltip v-if="description" :content="description" :delay-duration="300">
            <Info class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Tooltip>
        </div>
        <p v-if="customTitle" class="text-xs text-muted-foreground">{{ customTitle }}</p>
        <slot v-else name="subtitle" />
        <div v-if="respondents != null || weighted" class="flex items-center gap-1 text-xs text-muted-foreground">
          <Tooltip v-if="respondents != null" :content="respondentsHint" :disabled="!respondentsHint">
            <span class="w-fit tabular-nums">n={{ respondents }}</span>
          </Tooltip>
          <ResultsV2CardIndicators :weighted="weighted" />
        </div>
      </div>
      <!-- card-toolbar is excluded from the image capture (useCardDownload). The sub_type's
           selectors stay visible in view mode; the buttons only show on hover there. -->
      <div class="flex shrink-0 items-center gap-1">
        <div class="card-toolbar print:hidden">
          <slot name="toolbar" />
        </div>
        <div
          class="card-toolbar flex items-center gap-1 print:hidden"
          :class="{ 'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100': readonly }">
          <CardWidthToggle :card="card" :readonly="readonly" />
          <Tooltip :content="copied ? 'Copied!' : 'Copy image'" :delay-duration="1000">
            <button
              aria-label="Copy as image"
              class="inline-flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
              @click="copyCardToClipboard(card.id)">
              <Check v-if="copied" class="h-3.5 w-3.5 text-status-success-strong" />
              <Clipboard v-else class="h-3.5 w-3.5" />
            </button>
          </Tooltip>
          <Tooltip content="Download image" :delay-duration="1000">
            <button
              aria-label="Download as image"
              class="inline-flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
              @click="downloadCardAsImage(card.id, displayName)">
              <Download class="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>

    <LoadingState v-if="state === 'loading'" size="sm" class="h-48" />
    <EmptyState
      v-else-if="emptyState"
      :icon="emptyState.icon"
      :title="stateMessage || emptyState.title"
      :description="emptyState.description"
      class="py-10" />
    <slot v-else />

    <ResultsStyledBlock v-if="comment" :html="comment" :variant="card.options?.comment_style || 'info'" />
  </div>
</template>
